import { StyleSheet, View, Dimensions, TouchableOpacity } from 'react-native'
import React from 'react'
import { PrimaryText } from '@components/Common/PrimaryText/PrimaryText'
import { COLORS } from '@utils/colors'
import MuiButton from '@components/Common/MuiButton/MuiButton'
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import { resolveTipsConfigForOrg } from '@utils/sidebarTipSteps'
import { updateMemberInfo } from '@services/UserService'
import { useAuth } from '@context/AuthContext/AuthContext'
import { useOrganization } from '@context/OrganizationContext/OrganizationContext'
import useMediaQuery from '@hooks/useMediaQuery'
import { SidebarTipsProps } from '@Interfaces/Component/SidebarTipsInterface'
import type { ResponsiveValueOptions } from '@Interfaces/Hooks/UIInterface'
import { Box } from '@mui/material'

const { width, height } = Dimensions.get('window')

const SidebarTips: React.FC<SidebarTipsProps> = ({
  children,
  index = 0,
  totalSteps,
  path,
  onNext,
  show,
  isLastVisibleStep,
}) => {
  const { currentUser } = useAuth()
  const { selectedOrg, currentMember } = useOrganization()
  const { getResponsiveValue } = useMediaQuery()

  if (!show || currentMember?.hideTips) return <>{children}</>

  const tipsConfigResolved = resolveTipsConfigForOrg(selectedOrg)
  const tipData = tipsConfigResolved[path || '']

  if (!tipData) return <>{children}</>

  const { title, description, hideIcon, buttonTitle } = tipData

  const hideTips = () => {
    if (selectedOrg && currentUser) {
      updateMemberInfo(selectedOrg, currentUser, { hideTips: true })
    }
  }

  const styles = getStyles(getResponsiveValue)

  const denominator =
    typeof totalSteps === 'number' && totalSteps > 0
      ? totalSteps
      : Object.keys(tipsConfigResolved).length

  const finishLike = buttonTitle === 'Finish' || isLastVisibleStep

  const actionTitle = finishLike ? 'Finish' : buttonTitle || 'Next'
  const stripNextIcon = finishLike && (hideIcon || buttonTitle === 'Finish')

  return (
    <View>
      {children}

      <TouchableOpacity onPress={hideTips} style={styles.overlay} />

      <View style={styles.container}>
        <View style={[styles.wrapper, { top: index < 7 ? -45 : -185 }]}>
          <View style={[styles.triangle, { top: index < 7 ? '25%' : '75%' }]} />

          <View style={styles.centered}>
            <PrimaryText fontSize={14} fontWeight={700} color={COLORS.primary.neutral_400}>
              {index + 1}/{denominator}
            </PrimaryText>

            <PrimaryText
              fontSize={24}
              fontWeight={900}
              color={COLORS.primary.neutral_800}
              style={{ marginTop: 5 }}
            >
              {title}
            </PrimaryText>

            <Box
              component="div"
              sx={{
                color: COLORS.primary.neutral_800,
                marginTop: '11px',
                marginLeft: '20px',
                marginRight: '20px',
                lineHeight: '24px',
                fontSize: 16,
                fontFamily: 'Montserrat',
                '& b': { fontWeight: 700 },
              }}
            >
              {description}
            </Box>

            <View style={{ height: 15 }} />

            <View style={styles.buttonRow}>
              <TouchableOpacity onPress={hideTips}>
                <PrimaryText
                  fontSize={16}
                  fontWeight={700}
                  color={COLORS.primary.neutral_400}
                >
                  Hide Tips
                </PrimaryText>
              </TouchableOpacity>

              <MuiButton
                title={actionTitle}
                sx={{ width: 86, height: 29 }}
                variant="contained"
                endIcon={stripNextIcon ? null : <KeyboardArrowRightRoundedIcon />}
                onPress={() => {
                  onNext?.()
                  if (finishLike) hideTips()
                }}
              />
            </View>
          </View>
        </View>
      </View>
    </View>
  )
}

const getStyles = (getResponsiveValue: (value: ResponsiveValueOptions) => string) => {
  const displayMdUp = getResponsiveValue({ xs: 'none', sm: 'flex' })
  return StyleSheet.create({
    overlay: {
      width: width - 280,
      height,
      backgroundColor: 'rgba(0,0,0, 0.25)',
      position: 'fixed',
      top: 0,
      left: 280,
      zIndex: 950,
      display: displayMdUp,
    },
    container: {
      position: 'fixed',
      left: 340,
      zIndex: 999,
      display: displayMdUp,
    },
    wrapper: {
      top: -45,
      shadowColor: '#000',
      shadowOpacity: 0.15,
      shadowRadius: 15,
      shadowOffset: {
        width: 5,
        height: 5,
      },
      borderRadius: 15,
      backgroundColor: '#fff',
    },
    centered: {
      width: 394,
      minHeight: 271,
      backgroundColor: COLORS.background.white,
      borderRadius: 15,
      paddingHorizontal: 25,
      paddingVertical: 30,
    },
    triangle: {
      position: 'absolute',
      top: '25%',
      left: -30,
      backgroundColor: '#fff',
      clipPath: 'polygon(100% 0%, 100% 100%, 0% 50%)',
      width: 32,
      height: 32,
    },
    buttonRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: 'auto',
      marginLeft: 20,
    },
  })
}

export default SidebarTips
